import type { RefObject } from 'react'
import type { InputWithValidation } from '../types/EventDetailTypes'

export type ContainersForValidatableInputs = HTMLFormElement | HTMLDivElement | HTMLSpanElement | HTMLFieldSetElement

/**
 * @description
 * returns an array that exclusively contains inputs that would be validated after user input
 *
 * @param source -- can either be a RefObject that references a container that contains input elements, a form element,
 * or a NodeList directly from querySelectorAll. When an HTMLFormElement is provided, it also returns validatable
 * inputs from outside the bounds of the form tags
 *
 * @returns InputWithValidation[] -- an array that only contains input that would be validated by any form system on user input.
 * An important thing to note, is that if the RefObject references a form element, it also gathers related (compatible) elements
 * that are not necessarily contained within the tags of the form element
 * @returns null -- when source OR RefObject.current is null | undefined | or not a the right type of container
 * */
export const getArrayOfValidatableInputsFromSource = (
    source: NodeList | HTMLFormElement | RefObject<ContainersForValidatableInputs | null> | null,
): InputWithValidation[] | null => {
    const nodeArray: Node[] = []
    switch (true) {
        case source instanceof HTMLFormElement: {
            //@ts-ignore
            nodeArray.push(...Array.from(source.elements))
            break
        }
        case source instanceof NodeList: {
            //@ts-ignore
            nodeArray.push(...Array.from(source))
            break
        }
        case (source as RefObject<HTMLFormElement> | null)?.current?.elements instanceof HTMLFormControlsCollection: {
            //@ts-ignore
            nodeArray.push(...((source as RefObject<HTMLFormElement>)?.current?.elements ?? []))
            break
        }
        case (source as RefObject<HTMLDivElement | HTMLSpanElement | HTMLFieldSetElement | null>)?.current
            ?.childNodes instanceof NodeList: {
            const childNodes: NodeList | null =
                //@ts-ignore
                (source?.current?.childNodes satisfies NodeList | undefined | null) ?? null
            //@ts-ignore
            nodeArray.push(...Array.from(childNodes ?? []))
        }
        default:
            return null
    }

    return (
        nodeArray
            .filter(
                (node: Node) =>
                    node instanceof HTMLTextAreaElement ||
                    node instanceof HTMLInputElement ||
                    node instanceof HTMLSelectElement,
            )
            //@ts-ignore
            .filter((node: InputWithValidation) => node?.type !== 'hidden' && node?.type !== 'submit') as
            | InputWithValidation[]
            | null
    )
}
